import { cloneDeep, round } from 'lodash'
import quote360TemplateCostlineArray from 'src/data/360Template/costline.json'
import quoteAnimationAndLiveActionCostlineArray from 'src/data/animation/quoteAnimationAndLiveAction/costline.json'
import quoteAnimationOnlyCostlineArray from 'src/data/animation/quoteAnimationOnly/costline.json'
import cnc5chCostlineArray from 'src/data/cnc/5ch/costline.json'
import quoteDigitalCostlineArray from 'src/data/digital/costline.json'
import frenchApfpCostlineArray from 'src/data/french/apfp/costline.json'
import frenchPostProdCostlineArray from 'src/data/french/postProduction/costline.json'
import photoGridCostlineArray from 'src/data/photoGrid/costline.json'
import soundProdCostlineArray from 'src/data/soundProd/costline.json'
import aicpCostlineArray from 'src/data/usa/aicp/costline.json'
import type { ReduxQuote, ReduxRecapInfo } from 'src/types/apiType'
import type { QuoteType } from 'src/types/miscType'
import { removeExtraSpaces, updatedParseFloat } from 'src/utils/miscellaneous'
import { getCreateQuoteRecapInfoUtil } from 'src/utils/recapGridUtils/createQuoteRecapInfoUtil/getCreateQuoteRecapInfoUtil'

const calculateRowTotal = (row: ReduxRecapInfo) => {
	let shootNumber = 0
	let number = 0
	// By default the value for number & shootNumber is 0 we update the value if it exists in the grid
	if (row.provisionalShootNumber) shootNumber = row.provisionalShootNumber
	if (row.provisionalNumber) number = row.provisionalNumber

	if (
		row.provisionalQuantity &&
		row.provisionalRate &&
		(row.provisionalNumber || row.provisionalShootNumber)
	) {
		const total =
			row.provisionalQuantity *
				(number + shootNumber) *
				row.provisionalRate +
			(row.provisionalOtTotal ?? 0)

		return round(total, 2)
	}
	return null
}

const parseFloatAttributes = (row: any) => {
	const localRow = { ...row }
	if (localRow.provisionalQuantity || localRow.provisionalQuantity === '')
		localRow.provisionalQuantity = updatedParseFloat(
			localRow.provisionalQuantity
		)
	if (localRow.provisionalNumber || localRow.provisionalNumber === '')
		localRow.provisionalNumber = updatedParseFloat(
			localRow.provisionalNumber
		)
	if (
		localRow.provisionalShootNumber ||
		localRow.provisionalShootNumber === ''
	)
		localRow.provisionalShootNumber = updatedParseFloat(
			localRow.provisionalShootNumber
		)
	if (localRow.provisionalRate || localRow.provisionalRate === '') {
		localRow.provisionalRate = updatedParseFloat(localRow.provisionalRate)
	}
	if (localRow.provisionalOtTotal || localRow.provisionalOtTotal === '') {
		localRow.provisionalOtTotal = updatedParseFloat(
			localRow.provisionalOtTotal
		)
	}
	if (localRow.provisionalTotal || localRow.provisionalTotal === '')
		localRow.provisionalTotal = updatedParseFloat(localRow.provisionalTotal)
	if (localRow.actualTotal || localRow.actualTotal === '')
		localRow.actualTotal = updatedParseFloat(localRow.actualTotal)
	return localRow as ReduxRecapInfo
}

const calculateGridTotals = ({
	rows,
	quoteType,
	quoteDetails,
}: {
	rows: ReduxRecapInfo[]
	quoteType: QuoteType
	quoteDetails?: ReduxQuote | null
}) => {
	let localRows = cloneDeep(rows)
	// update sub category totals
	// * Aicp doesn't have payroll taxes category
	const payrollTaxItemsExist = localRows.some(
		(r) => r.category === 'Payroll Taxes'
	)
	if (payrollTaxItemsExist) {
		// update payroll taxes
		localRows = updatePayrollTaxes({ rows: localRows, quoteType })
	}
	localRows = updateSubCategories(localRows)
	// update category totals
	localRows = updateCategories(localRows)

	return localRows
}

const updateSubCategories = (rows: ReduxRecapInfo[]) => {
	const localRows = cloneDeep(rows)
	const subCategoryTotalRows = localRows.filter(
		(row) => row.costLineType === 'subCategoryTotal'
	)
	return subCategoryTotalRows.reduce((updatedRows, row) => {
		if (!row?.category || !row.subCategory) {
			return updatedRows
		}
		return updateSubCategoryTotal(
			updatedRows,
			row.category,
			row.subCategory
		)
	}, localRows)
}

const updateCategories = (rows: ReduxRecapInfo[]) => {
	const localRows = cloneDeep(rows)
	const categoryTotalRows = localRows.filter(
		(row) => row.costLineType === 'categoryTotal'
	)
	return categoryTotalRows.reduce((updatedRows, row) => {
		if (!row?.category) {
			return updatedRows
		}
		return updateCategoryTotal(updatedRows, row.category)
	}, localRows)
}

function getPayrollTaxRows(quoteType: QuoteType) {
	const payrollTaxRateMap = {} as {
		[key: string]: {
			provisionalTotal: number
			actualTotal: number
		}
	}
	switch (quoteType) {
		case 'frenchApfp':
			frenchApfpCostlineArray.map((costline) => {
				if (
					costline.category.toLowerCase().trim() ===
					'Payroll Taxes'.toLowerCase().trim()
				) {
					payrollTaxRateMap[
						removeExtraSpaces(costline.item).toLowerCase()
					] = {
						provisionalTotal: 0,
						actualTotal: 0,
					}
				}
				return costline
			})
			return payrollTaxRateMap
		case 'frenchPostProd':
			frenchPostProdCostlineArray.map((costline) => {
				if (
					costline.category.toLowerCase().trim() ===
					'Payroll Taxes'.toLowerCase().trim()
				) {
					payrollTaxRateMap[
						removeExtraSpaces(costline.item).toLowerCase()
					] = {
						provisionalTotal: 0,
						actualTotal: 0,
					}
				}
				return costline
			})
			return payrollTaxRateMap
		case 'aicp':
			aicpCostlineArray.map((costline) => {
				if (
					costline.category.toLowerCase().trim() ===
					'Payroll Taxes'.toLowerCase().trim()
				) {
					payrollTaxRateMap[
						removeExtraSpaces(costline.item).toLowerCase()
					] = {
						provisionalTotal: 0,
						actualTotal: 0,
					}
				}
				return costline
			})
			return payrollTaxRateMap
		case 'photoGrid':
			photoGridCostlineArray.map((costline) => {
				if (
					costline.category.toLowerCase().trim() ===
					'Payroll Taxes'.toLowerCase().trim()
				) {
					payrollTaxRateMap[
						removeExtraSpaces(costline.item).toLowerCase()
					] = {
						provisionalTotal: 0,
						actualTotal: 0,
					}
				}
				return costline
			})
			return payrollTaxRateMap
		case 'soundProd':
			soundProdCostlineArray.map((costline) => {
				if (
					costline.category.toLowerCase().trim() ===
					'Payroll Taxes'.toLowerCase().trim()
				) {
					payrollTaxRateMap[
						removeExtraSpaces(costline.item).toLowerCase()
					] = {
						provisionalTotal: 0,
						actualTotal: 0,
					}
				}
				return costline
			})
			return payrollTaxRateMap
		case 'cnc5ch':
			cnc5chCostlineArray.map((costline) => {
				if (
					costline.category.toLowerCase().trim() ===
					'Payroll Taxes'.toLowerCase().trim()
				) {
					payrollTaxRateMap[
						removeExtraSpaces(costline.item).toLowerCase()
					] = {
						provisionalTotal: 0,
						actualTotal: 0,
					}
				}
				return costline
			})
			return payrollTaxRateMap
		case 'quoteAnimationOnly':
			quoteAnimationOnlyCostlineArray.map((costline) => {
				if (
					costline.category.toLowerCase().trim() ===
					'Payroll Taxes'.toLowerCase().trim()
				) {
					payrollTaxRateMap[
						removeExtraSpaces(costline.item).toLowerCase()
					] = {
						provisionalTotal: 0,
						actualTotal: 0,
					}
				}
				return costline
			})
			return payrollTaxRateMap
		case 'quoteAnimationAndLiveAction':
			quoteAnimationAndLiveActionCostlineArray.map((costline) => {
				if (
					costline.category.toLowerCase().trim() ===
					'Payroll Taxes'.toLowerCase().trim()
				) {
					payrollTaxRateMap[
						removeExtraSpaces(costline.item).toLowerCase()
					] = {
						provisionalTotal: 0,
						actualTotal: 0,
					}
				}
				return costline
			})
			return payrollTaxRateMap
		case 'quoteDigital':
			quoteDigitalCostlineArray.map((costline) => {
				if (
					costline.category.toLowerCase().trim() ===
					'Payroll Taxes'.toLowerCase().trim()
				) {
					payrollTaxRateMap[
						removeExtraSpaces(costline.item).toLowerCase()
					] = {
						provisionalTotal: 0,
						actualTotal: 0,
					}
				}
				return costline
			})
			return payrollTaxRateMap
		case '360Template':
			quote360TemplateCostlineArray.map((costline) => {
				if (
					costline.category.toLowerCase().trim() ===
					'Payroll Taxes'.toLowerCase().trim()
				) {
					payrollTaxRateMap[
						removeExtraSpaces(costline.item).toLowerCase()
					] = {
						provisionalTotal: 0,
						actualTotal: 0,
					}
				}
				return costline
			})
			return payrollTaxRateMap
		case 'blankQuote':
			return payrollTaxRateMap

		default: {
			const tsCheck: never = quoteType
			console.log('tsCheck: ', tsCheck)
			return payrollTaxRateMap
		}
	}
}

const updatePayrollTaxes = ({
	rows,
	quoteType,
}: {
	rows: ReduxRecapInfo[]
	quoteType: QuoteType
}) => {
	const localRows = cloneDeep(rows)
	const payrollTaxRateMap = getPayrollTaxRows(quoteType)

	for (const r of localRows) {
		if (r.provisionalPayrollTaxType) {
			payrollTaxRateMap[
				removeExtraSpaces(r.provisionalPayrollTaxType).toLowerCase()
			].provisionalTotal += r?.provisionalTotal ?? 0
		}
		if (r.actualPayrollTaxType) {
			payrollTaxRateMap[
				removeExtraSpaces(r.actualPayrollTaxType).toLowerCase()
			].actualTotal += r.actualTotal ?? 0
		}
	}

	for (const pItem of Object.keys(payrollTaxRateMap)) {
		const payrollTaxItem = removeExtraSpaces(pItem).toLowerCase()
		const payrollTaxItemIndexInRows = localRows.findIndex(
			(r) =>
				r.category === 'Payroll Taxes' &&
				r.item &&
				removeExtraSpaces(r.item).toLowerCase() === payrollTaxItem
		)
		if (payrollTaxItemIndexInRows !== -1) {
			const payrollTaxItemProvisionalTotal = round(
				(localRows[payrollTaxItemIndexInRows].quantity ?? 0) *
					payrollTaxRateMap[payrollTaxItem].provisionalTotal,
				2
			)
			const payrollTaxItemActualTotal = round(
				(localRows[payrollTaxItemIndexInRows].quantity ?? 0) *
					payrollTaxRateMap[payrollTaxItem].actualTotal,
				2
			)
			localRows[payrollTaxItemIndexInRows].provisionalTotal =
				payrollTaxItemProvisionalTotal
			localRows[payrollTaxItemIndexInRows].actualTotal =
				payrollTaxItemActualTotal
		}
	}

	return localRows
}

const updateSubCategoryTotal = (
	rows: ReduxRecapInfo[],
	category: string,
	subCategory: string
) => {
	const localRows = cloneDeep(rows)
	const subCategoryTotalsObject = localRows.reduce(
		(totalsObject, r) => {
			if (
				r.category === category &&
				r.subCategory === subCategory &&
				(r.costLineType === 'costLine' ||
					r.costLineType === 'subCategoryTaxes') &&
				(r.provisionalTotal || r.actualTotal || r.purchaseTotal)
			) {
				if (r.provisionalTotal)
					totalsObject.provisionalTotal += r.provisionalTotal
				if (r.actualTotal) totalsObject.actualTotal += r.actualTotal
				if (r.purchaseTotal)
					totalsObject.purchaseTotal += r.purchaseTotal
			}
			return totalsObject
		},
		{ provisionalTotal: 0, actualTotal: 0, purchaseTotal: 0 }
	)
	subCategoryTotalsObject.provisionalTotal = round(
		subCategoryTotalsObject.provisionalTotal,
		2
	)
	subCategoryTotalsObject.actualTotal = round(
		subCategoryTotalsObject.actualTotal,
		2
	)
	subCategoryTotalsObject.purchaseTotal = round(
		subCategoryTotalsObject.purchaseTotal,
		2
	)

	const subCategoryTotalRowIndex = localRows.findIndex(
		(r) =>
			r.costLineType === 'subCategoryTotal' &&
			r.category === category &&
			r.subCategory === subCategory
	)
	if (subCategoryTotalRowIndex === -1) {
		console.log('subCategory: ', subCategory)
		console.log('category: ', category)
	}
	localRows[subCategoryTotalRowIndex].provisionalTotal =
		subCategoryTotalsObject.provisionalTotal
	localRows[subCategoryTotalRowIndex].actualTotal =
		subCategoryTotalsObject.actualTotal
	localRows[subCategoryTotalRowIndex].purchaseTotal =
		subCategoryTotalsObject.purchaseTotal

	return localRows
}

const updateCategoryTotal = (rows: ReduxRecapInfo[], category: string) => {
	const localRows = cloneDeep(rows)
	const categoryTotalsObject = localRows.reduce(
		(totalsObject, r) => {
			if (
				r.category === category &&
				r.costLineType === 'subCategoryTotal' &&
				(r.provisionalTotal || r.actualTotal || r.purchaseTotal)
			) {
				if (r.provisionalTotal)
					totalsObject.provisionalTotal += r.provisionalTotal
				if (r.actualTotal) totalsObject.actualTotal += r.actualTotal
				if (r.purchaseTotal)
					totalsObject.purchaseTotal += r.purchaseTotal
			}
			return totalsObject
		},
		{ provisionalTotal: 0, actualTotal: 0, purchaseTotal: 0 }
	)
	categoryTotalsObject.provisionalTotal = round(
		categoryTotalsObject.provisionalTotal,
		2
	)
	categoryTotalsObject.actualTotal = round(
		categoryTotalsObject.actualTotal,
		2
	)
	categoryTotalsObject.purchaseTotal = round(
		categoryTotalsObject.purchaseTotal,
		2
	)

	const categoryTotalRowIndex = localRows.findIndex(
		(r) => r.costLineType === 'categoryTotal' && r.category === category
	)
	//
	localRows[categoryTotalRowIndex].provisionalTotal =
		categoryTotalsObject.provisionalTotal
	localRows[categoryTotalRowIndex].actualTotal =
		categoryTotalsObject.actualTotal
	localRows[categoryTotalRowIndex].purchaseTotal =
		categoryTotalsObject.purchaseTotal

	return localRows
}

const calculateGridTableRowIndex = ({
	rows,
	recalculateId = false,
	quoteType,
}: {
	rows: ReduxRecapInfo[]
	recalculateId?: boolean
	quoteType: QuoteType
}) => {
	let localRows = [...rows]
	for (let i = 0; i < rows.length; i++) {
		localRows[i].tableRowIndex = i
	}
	if (recalculateId) {
		// We find the first costline that has the child freelancers field filled
		const quoteDetails = localRows.find(
			(row) => row?.childFreelancers && row?.childFreelancers?.length > 0
		)
		if (!quoteDetails?.quoteId || quoteDetails?.childFreelancers) {
			return localRows
		}
		const { createRecapInfoCostLineIdsAndLabels } =
			getCreateQuoteRecapInfoUtil(quoteType)

		localRows = createRecapInfoCostLineIdsAndLabels(
			localRows,
			quoteDetails?.quoteId,
			quoteDetails?.tenantId,
			quoteDetails?.childFreelancers as string[]
		)
	}
	return localRows
}

const calculateGridTotalsAndIndex = ({
	rows,
	recalculateId,
	quoteType,
}: {
	rows: ReduxRecapInfo[]
	recalculateId?: boolean
	quoteType: QuoteType
}) => {
	let localRows = [...rows]
	localRows = calculateGridTotals({ rows: localRows, quoteType })
	localRows = calculateGridTableRowIndex({
		rows: localRows,
		recalculateId,
		quoteType,
	})
	return localRows
}

export {
	calculateGridTotals,
	calculateGridTotalsAndIndex,
	calculateRowTotal,
	parseFloatAttributes,
}
